import type { TFunction } from 'i18next';
import type { DeviationAction, DeviationStatus, DueState } from '../../../api/safetyRounds';
import { Routes } from '../../../enums/Routes';

export const deviationPath = (id: number) => Routes.Deviation.replace(':deviationId', String(id));

export const STATUS_COLORS: Record<DeviationStatus, 'danger' | 'warning' | 'success' | 'info'> = {
  OPEN: 'danger',
  FIXED: 'warning',
  AWAITING_DECISION: 'info',
  CLOSED: 'success',
  ACCEPTED: 'success',
};

/** Red past the deadline, yellow with seven days or less left */
export const DUE_COLORS: Record<DueState, 'danger' | 'warning' | 'neutral' | 'success'> = {
  overdue: 'danger',
  soon: 'warning',
  onTime: 'neutral',
  done: 'success',
};

/** The choices of the one responsible, and of the safety representative and general manager */
export const RESPONSIBLE_CHOICES = ['markFixed', 'moveDeadline', 'cannotFix'] as const;
export const CHECK_CHOICES = ['confirmFixed', 'sendBack'] as const;
export const DECISION_CHOICES = ['acceptRisk', 'mustBeFixed'] as const;

export type Choice = (typeof RESPONSIBLE_CHOICES)[number] | (typeof CHECK_CHOICES)[number] | (typeof DECISION_CHOICES)[number];

/** Which group of choices the user has now, in the order the page shows them */
export const choicesFor = (actions: DeviationAction[]): Choice[] =>
  [...RESPONSIBLE_CHOICES, ...CHECK_CHOICES, ...DECISION_CHOICES].filter((choice) => actions.includes(choice));

/** Every text tied to a change of status is required, except confirming a fix */
export const needsComment = (choice: Choice) => choice !== 'confirmFixed';

export type ChoiceForm = { comment: string; dueDate: string };

export type ChoiceError = { field: 'comment' | 'dueDate'; message: string };

export const choiceErrors = (choice: Choice, form: ChoiceForm, currentDue: string, t: TFunction): ChoiceError[] => {
  const K = 'safetyRounds.deviation.errors';
  return [
    ...(choice === 'moveDeadline' && !(/^\d{4}-\d{2}-\d{2}$/.test(form.dueDate) && form.dueDate > currentDue)
      ? [{ field: 'dueDate' as const, message: t(`${K}.dueDate`) }]
      : []),
    ...(needsComment(choice) && !form.comment.trim()
      ? [{ field: 'comment' as const, message: t(`${K}.comment.${choice}`) }]
      : []),
  ];
};
